import {
  MetricsOption,
  TrendCategory,
  Trend,
  trendMetricsMap,
  TrendMetricsMapType,
} from '@amzn/aet-galactus-kit';
import moment from 'moment';
import { SortDirection } from '../../../../Types/SortDirection';
import {
  AssociateCardSummary,
  AssociateInsight,
  ReportingTeamUser,
  TeamProfile,
} from '../../../../Types';
import {
  BottomQuartileCategory,
  BottomQuartileData,
  TeamDetailsMetricsPerUser,
  TrendAndDetailsMetrics,
  TrendCategoryDefaultName,
} from './types';
import {
  isTmCheck,
  MetricBooleanMap,
  PERCENTAGE_TABLE,
} from '../../../Dashboard/DashboardComponents/LighthouseMetrics/helper';
import { isInteger } from 'lodash';
import { TableSortDirection } from '@amzn/meridian/table/table';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import DateCompare from '../../../../Types/DateCompare';
import { Feature, isFeatureEnabled } from '../../../../config/FeaturesConfig';

const BQ_START_DATE = moment('2024-10-01'); // BQ feed genesis from Oct 2024.
const BQ_LOOKBACK_MONTHS = 6; // Limit to maximum past 6 months.

export const formatMetric = (value: string): string => {
  if (!value || value === 'null') {
    return '--';
  }
  if (value.includes(':')) {
    return `${value}s`;
  }

  const number = parseFloat(value);
  if (!isNaN(number)) {
    return number.toFixed(1) + '%';
  } else {
    return '--';
  }
};

export interface CachedMetric {
  key: string;
  value: any;
}

export function findDateLabel(input: string): string {
  // week format of yyyy-wk
  if (input.match(/^\d{4}-\d{2}$/)) {
    // GGGG refers to the year of the week. This may be different from YYYY
    // When part of the week is from 2024 and part of it is from 2025. e.g. 2024-12-30 (Monday)
    // In such cases, GGGG refers to the new year (e..g 2025) and YYYY refers to the previous year (e.g. 2024).
    const startDate = moment(input, 'GGGG-WW', true).add(-1, 'd');
    const endDate = startDate.clone().add(6, 'days');
    return input + ` (${startDate.format('M/D')} - ${endDate.format('M/D')})`;
  }

  // month format of yyyy-mm-dd1/yyyy-mm-dd2
  if (input.match(/^\d{4}-\d{2}-\d{2}\/\d{4}-\d{2}-\d{2}$/)) {
    const [firstDate, secondDate] = input.split('/');

    const [year1, month1, day1] = firstDate.split('-').map(Number);
    const [year2, month2, day2] = secondDate.split('-').map(Number);

    const first = new Date(Date.UTC(year1, month1 - 1, day1));
    const second = new Date(Date.UTC(year2, month2 - 1, day2));

    return `${first.getUTCFullYear()}-${
      first.getUTCMonth() + 1
    }/${first.getUTCDate()} - ${
      second.getUTCMonth() + 1
    }/${second.getUTCDate()}`;
  }
  return input;
}

export enum ViewByToggle {
  GRAPH = 'graph',
  TABLE = 'table',
}

export function getMetrics(
  vertical: string,
  category: string
): MetricsOption[] {
  const resultMetrics: MetricsOption[] = [];
  const metricsMap: TrendMetricsMapType = trendMetricsMap;
  const categories = metricsMap[vertical]?.[category];

  if (categories) {
    const metrics = Object.keys(categories);

    metrics.forEach(metricItem => {
      if (Object.values(MetricsOption).includes(metricItem as MetricsOption)) {
        resultMetrics.push(metricItem as MetricsOption);
      }
    });
  }

  return filterCCXNeg(resultMetrics);
}

export function getSubMetrics(
  vertical: string,
  category: string,
  metrics: MetricsOption[]
): MetricsOption[] {
  const resultSubMetrics: Set<MetricsOption> = new Set();
  const metricsMap: TrendMetricsMapType = trendMetricsMap;
  const categoriesMap = metricsMap[vertical]?.[category];

  if (categoriesMap) {
    metrics.forEach((metric: MetricsOption) => {
      const subMetricsMap = categoriesMap[metric as keyof typeof categoriesMap];
      if (Array.isArray(subMetricsMap)) {
        (subMetricsMap as MetricsOption[]).forEach((item: MetricsOption) =>
          resultSubMetrics.add(item)
        );
      }
    });
  }

  return filterCCXNeg(Array.from(resultSubMetrics));
}

export function sortMetric(
  timestamps: string[],
  dataPerTimestamp: Record<string, any>,
  sortColumn: string,
  sortDirection: SortDirection
): string[] {
  return timestamps.sort((a, b) => {
    let valA = dataPerTimestamp[a]?.[sortColumn] || '';
    let valB = dataPerTimestamp[b]?.[sortColumn] || '';

    valA = valA.replace(/:/g, '.');
    valB = valB.replace(/:/g, '.');

    const numA = parseFloat(valA);
    const numB = parseFloat(valB);

    const isNumA = !isNaN(numA);
    const isNumB = !isNaN(numB);

    let comparison = 0;

    if (isNumA && isNumB) {
      comparison = numA - numB;
    } else {
      comparison = valA.localeCompare(valB);
    }

    if (sortDirection === SortDirection.DESCENDING) {
      comparison = -comparison;
    }

    return comparison;
  });
}

export function computeColumnWidthAsPercent(
  metricColumnCount?: number
): string {
  if (metricColumnCount === undefined) {
    return '0%';
  } else if (metricColumnCount === 0) {
    return '100%';
  }
  const result = (100 / metricColumnCount).toFixed(2);
  return `${result}%`;
}

export function getReportingTeamForDirectReports(
  reportingTeam: ReportingTeamUser[]
): ReportingTeamUser[] {
  return reportingTeam.filter(
    member => member.relatedUser.relationToUser === 'DIRECT_REPORT'
  );
}

export function filterMetricsAndSubmetrics(
  trendMetrics: TrendAndDetailsMetrics,
  vertical: string,
  category: string,
  metricTypes: string[],
  subMetricTypes: string[]
): TrendAndDetailsMetrics;

export function filterMetricsAndSubmetrics(
  trendMetrics: TrendAndDetailsMetrics[],
  vertical: string,
  category: string,
  metricTypes: string[],
  subMetricTypes: string[]
): TrendAndDetailsMetrics[];

export function filterMetricsAndSubmetrics(
  trendMetrics: TrendAndDetailsMetrics | TrendAndDetailsMetrics[],
  vertical: string,
  category: string,
  metricTypes: string[],
  subMetricTypes: string[]
): TrendAndDetailsMetrics | TrendAndDetailsMetrics[] {
  let newMetrics: MetricsOption[] = [];
  let newSubMetrics: MetricsOption[] = [];
  const enableHighSilence = isFeatureEnabled(Feature.HIGH_SILENCE_RATE);

  if (metricTypes.some(item => item.toLowerCase() === 'all'.toLowerCase())) {
    newMetrics = getMetrics(vertical, category);
  } else {
    metricTypes.forEach(item => {
      if (Object.values(MetricsOption).includes(item as MetricsOption)) {
        const metricToAdd = item as MetricsOption;
        if (!newMetrics.includes(metricToAdd)) {
          newMetrics.push(metricToAdd);
        }
      }
    });
  }

  if (subMetricTypes.some(item => item.toLowerCase() === 'all'.toLowerCase())) {
    newSubMetrics = getSubMetrics(vertical, category, newMetrics);
  } else {
    subMetricTypes.forEach(item => {
      if (Object.values(MetricsOption).includes(item as MetricsOption)) {
        const subMetricToAdd = item as MetricsOption;
        if (!newSubMetrics.includes(subMetricToAdd)) {
          newSubMetrics.push(subMetricToAdd);
        }
      }
    });
  }

  if (!enableHighSilence) {
    newMetrics = newMetrics.filter(
      metric => metric !== MetricsOption.HIGH_SILENCE_RATE
    );
    newSubMetrics = newSubMetrics.filter(
      metric => metric !== MetricsOption.HIGH_SILENCE_RATE
    );
  }

  const combinedMetrics: MetricsOption[] = [...newMetrics, ...newSubMetrics];

  if (Array.isArray(trendMetrics)) {
    return trendMetrics.map(metrics =>
      filterTrendAndDetailsMetrics(metrics, combinedMetrics)
    );
  } else {
    return filterTrendAndDetailsMetrics(trendMetrics, combinedMetrics);
  }
}

export function filterTrendAndDetailsMetrics(
  trendMetrics: TrendAndDetailsMetrics,
  combinedMetrics: string[]
): TrendAndDetailsMetrics {
  if (!trendMetrics.metricDatas || !trendMetrics.metricDatas.trend) {
    return trendMetrics;
  }
  const filteredTrendMetrics = {
    ...trendMetrics,
    metricTypes: trendMetrics.metricTypes.filter(metricTypeItem =>
      combinedMetrics.includes(metricTypeItem)
    ),
    metricDatas: {
      ...trendMetrics.metricDatas,
      trend: trendMetrics.metricDatas.trend.filter(trendItem =>
        combinedMetrics.includes(trendItem.name)
      ),
    },
  };

  return filteredTrendMetrics;
}

export const getTrendCategoryStringIdAndDefaultMessage = (
  category: TrendCategory
): {
  id: string;
  defaultMessage: string;
} => {
  const stringIdAndDefaultMessage = category
    ? TrendCategoryDefaultName.get(category)
    : '';
  return {
    id: stringIdAndDefaultMessage?.[0] ?? '',
    defaultMessage: stringIdAndDefaultMessage?.[1] ?? '',
  };
};

export type ChartData = {
  [key: string]: number | null | string;
};

export function transformTeamDetailsData(
  trends: TrendAndDetailsMetrics[],
  associateInsights?: AssociateInsight[]
): TeamDetailsMetricsPerUser {
  const dataPerUser: TeamDetailsMetricsPerUser = {};
  trends.map((user: TrendAndDetailsMetrics) => {
    if (!user.metricDatas || !user.metricDatas.trend) return {};
    const userID = user.userID;
    let isPercentage = false;
    if (
      !trends[0].metricDatas?.trend ||
      trends[0].metricDatas?.trend.length > 0
    ) {
      isPercentage =
        PERCENTAGE_TABLE[
          user.metricDatas?.trend[0].name as keyof MetricBooleanMap
        ];
    }

    // eslint-disable-next-line
    user.metricDatas?.trend?.forEach((metric: Trend) => {
      const metricName = metric.name;

      // filter out users who are not bottom quartile for that metrics
      if (associateInsights) {
        const isBottom =
          associateInsights
            .find(associateInsight =>
              associateInsight.associate.includes(user.userID)
            )
            ?.bottomQuartileMetrics.find(
              metric => metric.metricName === metricName
            ) ?? false;
        if (!isBottom) return;
      }
      metric.trendDatapoint.forEach(dp => {
        const datapointValue = dp.datapoint;
        const networkAverage = dp.networkAverage;
        if (!dataPerUser[userID]) {
          dataPerUser[userID] = {};
        }

        let rawDatapointValue;
        let rawNetworkValue;

        if (isPercentage) {
          dataPerUser[userID][metricName] = convertToPercent(datapointValue);
          dataPerUser[userID][`${metricName}NetAvg`] =
            convertToPercent(networkAverage);

          rawDatapointValue =
            datapointValue !== null ? Number(datapointValue) : 0;
          rawNetworkValue =
            networkAverage !== null ? Number(networkAverage) : 0;
        } else {
          dataPerUser[userID][metricName] = convertToTime(datapointValue);
          dataPerUser[userID][`${metricName}NetAvg`] =
            convertToTime(networkAverage);
          rawDatapointValue =
            datapointValue !== null ? convertToSeconds(datapointValue) : 0;
          rawNetworkValue =
            networkAverage !== null ? convertToSeconds(networkAverage) : 0;
        }

        const difference =
          datapointValue === null || networkAverage === null
            ? 0
            : rawDatapointValue - rawNetworkValue;

        dataPerUser[userID][`${metricName}BenchmarkDiff`] =
          difference.toString();
      });
    });
  });
  return dataPerUser;
}

export function transformTrendData(trends: Trend[]): ChartData[] {
  const timestampMap: { [key: string]: any } = {};
  const isPercentage =
    PERCENTAGE_TABLE[trends[0].name as keyof MetricBooleanMap];
  trends.forEach(trend => {
    trend.trendDatapoint.forEach(datapoint => {
      const { timestamp, datapoint: value, networkAverage } = datapoint;
      if (!timestampMap[timestamp]) {
        timestampMap[timestamp] = { timestamp };
      }
      if (isPercentage) {
        timestampMap[timestamp][`${trend.name}`] = convertToPercent(value);
        timestampMap[timestamp][`${trend.name}#netavg`] =
          convertToPercent(networkAverage);
      } else {
        timestampMap[timestamp][`${trend.name}`] = convertToTime(value);
        timestampMap[timestamp][`${trend.name}#netavg`] =
          convertToTime(networkAverage);
      }
    });
  });

  return Object.values(timestampMap);
}

export function calculateChartDataMinMax(
  chartData: ChartData[],
  isPercentage: boolean
): {
  dataMin: number;
  dataMax: number;
} {
  let min: number | null = null;
  let max: number | null = null;

  chartData.forEach(dataPoint => {
    Object.entries(dataPoint).forEach(([key, value]) => {
      if (
        key !== 'timestamp' &&
        value !== 'null' &&
        typeof value === 'string'
      ) {
        if (isPercentage) {
          const parsedValue = parseFloat(value);

          if (!isNaN(parsedValue)) {
            if (min === null || parsedValue < min) {
              min = parsedValue;
            }
            if (max === null || parsedValue > max) {
              max = parsedValue;
            }
          }
        } else {
          const parsedValue = parseFloat(value.replace(':', '.'));

          if (!isNaN(parsedValue)) {
            if (min === null || parsedValue < min) {
              min = parsedValue;
            }
            if (max === null || parsedValue > max) {
              max = parsedValue;
            }
          }
        }
      }
    });
  });

  return {
    dataMin: min !== null ? min : 0,
    dataMax: max !== null ? max : 0,
  };
}

export function convertToPercent(value: string | null): string {
  if (value === 'null' || value === null) return 'null';
  const numberValue = parseFloat(value) * 100;
  return numberValue.toFixed(2);
}

export function convertToTime(value: string | null): string {
  if (value === 'null' || value === null) return 'null';
  const numberValue = parseFloat(value.replace(':', '.'));
  return numberValue.toFixed(2);
}

export function convertToSeconds(time: string) {
  if (!time) return 0;
  const [minutes, seconds] = time.split(':').map(Number);
  return minutes * 60 + seconds;
}

export function generateTickRange(min: number, max: number): number[] {
  const result: number[] = [];
  let current = min;

  result.push(parseFloat(current.toFixed(2)));
  current = Math.ceil(current * 10) / 10;

  while (current < max) {
    const lastAdded = result[result.length - 1];
    if (current - lastAdded >= 5) {
      result.push(parseFloat(current.toFixed(2)));
    }
    current += 10;
  }

  if (result[result.length - 1] !== max) {
    result.push(parseFloat(max.toFixed(2)));
  }

  return result;
}

export const chartLineColors = [
  '#2C9F93', // Turquoise
  '#C3E9E7', // Turquoise light
  '#614FD9', // Violet
  '#C9A8FF', // Violet light
  '#EB64D7', // Purple
  '#ECA8E0', // Purple light
  '#DC3C71', // Pink
  '#FFB5CA', // Pink light
  '#708090', // Slate
  '#B6BDC6', // Slate light
  '#D4780C', // Tangerine
  '#FFCB96', // Tangerine light
  '#348118', // Forest
  '#ABD994', // Forest light
  '#DE1B1B', // Crimson
  '#FFB09F', // Crimson light
  '#DAA500', // Gold
  '#FFE8B1', // Gold light
];

export function formatMetricName(metricName: string): string {
  return metricName.replace(/#netavg/gi, ' Network Benchmark');
}

export function getBenchmarkSuffix(metricName: string): string {
  if (metricName.includes('#netavg')) {
    return '  Network Benchmark';
  }
  return '';
}

export function findAliasFromIdWithPrefix(
  fullIdWithPrefix: string,
  teamProfile: TeamProfile
): string {
  return (
    findUserFromIdWithPrefix(fullIdWithPrefix, teamProfile)?.relatedUser
      .alias ?? ''
  );
}

export function findFullNameFromIdWithPrefix(
  fullIdWithPrefix: string,
  teamProfile: TeamProfile
): string {
  return (
    findUserFromIdWithPrefix(fullIdWithPrefix, teamProfile)?.relatedUser
      .displayName ?? ''
  );
}

function findUserFromIdWithPrefix(
  fullIdWithPrefix: string,
  teamProfile: TeamProfile
): ReportingTeamUser | undefined {
  const team = teamProfile.reportingTeam;
  const foundUser = team.find(
    user =>
      user.relatedUser.userID === findUserIdFromEntityPrefix(fullIdWithPrefix)
  );
  return foundUser;
}

function findUserIdFromEntityPrefix(fullIdWithPrefix: string): string {
  return fullIdWithPrefix.split(':')[1];
}

export function hasValidDataPoints(data: TrendAndDetailsMetrics): boolean {
  let foundValidData = false;
  if (!data.metricDatas || !data.metricDatas.trend) return false;
  data.metricDatas.trend.forEach(metric => {
    metric.trendDatapoint.forEach(datapointObj => {
      if (
        (datapointObj.datapoint !== null &&
          datapointObj.datapoint !== 'null') ||
        (datapointObj.networkAverage !== null &&
          datapointObj.networkAverage !== 'null')
      ) {
        foundValidData = true;
      }
    });
  });

  return foundValidData;
}

export function hasChartDataValidDataPoints(data: ChartData[]): boolean {
  return data.some(entry => {
    return Object.keys(entry).some(key => {
      if (key !== 'timestamp') {
        const value = entry[key];
        return value !== 'null' && value !== null;
      }
      return false;
    });
  });
}

export function hasSingleNonNullValue(data: ChartData[], key: string): boolean {
  const nonNullCount = data.filter(entry => entry[key] !== 'null').length;
  return nonNullCount === 1;
}

export function sortChartDataByTimestamp(data: ChartData[]): ChartData[] {
  return data.sort((a, b) => {
    if (typeof a.timestamp !== 'string') return 1;
    if (typeof b.timestamp !== 'string') return -1;

    const startDateA = new Date(a.timestamp.split('/')[0]);
    const startDateB = new Date(b.timestamp.split('/')[0]);

    return startDateA.getTime() - startDateB.getTime();
  });
}

export const parseTrendFixedString = (
  value: number,
  isPercentage: boolean,
  isTrend: boolean
): string => {
  if (Number.isNaN(value)) return '--';
  if (!isPercentage) {
    const parts = value.toFixed(2).split('.');
    return `${parts[0]}:${parts[1].padEnd(2, '0')}`;
  }

  const parsedValueAPC = isPercentage ? value : value;

  const parsedValueAIC = isInteger(parsedValueAPC)
    ? parseInt(`${parsedValueAPC}`).toString()
    : parsedValueAPC.toFixed(1).toString();

  const parsedFixedString =
    isPercentage && isTrend
      ? parsedValueAIC
      : isPercentage
        ? `${parsedValueAIC}%`
        : parsedValueAIC;

  return parsedFixedString;
};

export function getTeamDirectReports(profile: TeamProfile) {
  return isTmCheck(profile)
    ? profile.reportingTeam
        .map(member => member.relatedUser)
        .filter(user => user.relationToUser === 'DIRECT_REPORT')
    : [];
}

export const getTeamIsTMForDirectReports = (profile: TeamProfile) => {
  return isTmCheck(profile)
    ? profile.reportingTeam
        .filter(member => member.relatedUser.relationToUser === 'DIRECT_REPORT')
        .map(member => member.isManager)
    : [];
};

export function isPercentage(metricName: string): boolean {
  const name = metricName.replace(/#netavg/gi, '');
  return PERCENTAGE_TABLE[name as keyof MetricBooleanMap] || false;
}

export const BOTTOM_QUARTILE_TABLE_SIZE = 10;

const timeToSeconds = (time: string): number => {
  const [minutes, seconds] = time.split(':').map(Number);
  return minutes * 60 + seconds;
};

const secondsToTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.abs(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

export function computeMetricDifference(
  metric: string,
  benchmark: string,
  invertCalculation: boolean = false
): string {
  if (metric.includes(':') && benchmark.includes(':')) {
    const metricSeconds = timeToSeconds(metric);
    const benchmarkSeconds = timeToSeconds(benchmark);
    const difference = invertCalculation
      ? metricSeconds - benchmarkSeconds
      : benchmarkSeconds - metricSeconds;
    const sign = Math.sign(difference) === -1 ? '-' : '';
    return `${sign}${secondsToTime(Math.abs(difference))}`;
  } else {
    const metricNumber = parseFloat(metric);
    const benchmarkNumber = parseFloat(benchmark);
    const difference = invertCalculation
      ? (metricNumber - benchmarkNumber) * 100
      : (benchmarkNumber - metricNumber) * 100;
    return `${difference.toFixed(1)}%`;
  }
}

export enum BottomQuartileTableColumns {
  Name = 'name',
  Metric_Name = 'metric_name',
  Metric_Value = 'metric_value',
  Benchmark = 'benchmark',
}

export const metricToNumber = (metricValue: string): number => {
  if (!metricValue.includes(':')) {
    return Number(metricValue.replace('%', '').trim());
  } else {
    return timeToSeconds(metricValue);
  }
};

export const sortBottomQuartile = (
  key: BottomQuartileTableColumns,
  sortDirection: TableSortDirection
) => {
  return (a: BottomQuartileData, b: BottomQuartileData) => {
    let sortResult, valueA, valueB, convertedValueA, convertedValueB;
    switch (key) {
      case BottomQuartileTableColumns.Benchmark:
        valueA = a.benchmarkDiff;
        valueB = b.benchmarkDiff;
        break;
      case BottomQuartileTableColumns.Metric_Name:
        valueA = a.metricName;
        valueB = b.metricName;
        break;
      case BottomQuartileTableColumns.Metric_Value:
        valueA = a.metricValue;
        valueB = b.metricValue;
        break;
      case BottomQuartileTableColumns.Name:
        valueA = a.userFullName;
        valueB = b.userFullName;
        break;
      default:
        return 0; // should never get here
    }
    if (valueA === undefined && valueB === undefined) {
      return 0;
    } else if (valueA === undefined) {
      return 1;
    } else if (valueB === undefined) {
      return -1;
    }
    switch (key) {
      case BottomQuartileTableColumns.Benchmark:
      case BottomQuartileTableColumns.Metric_Value:
        convertedValueA = metricToNumber(valueA);
        convertedValueB = metricToNumber(valueB);
        sortResult =
          convertedValueA > convertedValueB
            ? 1
            : convertedValueA < convertedValueB
              ? -1
              : 0;
        break;
      case BottomQuartileTableColumns.Metric_Name:
      case BottomQuartileTableColumns.Name:
        sortResult = valueA.localeCompare(valueB, undefined, {
          sensitivity: 'base',
        });
        break;
      default:
        return 0; // should never get here
    }

    return sortDirection === SortDirection.ASCENDING ? sortResult : -sortResult;
  };
};

/**
 * We want to show 6 month of BQ data. But nothing before Oct 2024.
 * @returns Max look back goes to 6 months. returns in range <-6, 0>
 */
export function getLookbackForBQ(): number {
  const today = moment();

  // Calculate months difference
  const monthsDifference = today.diff(BQ_START_DATE, 'months');

  return -Math.min(BQ_LOOKBACK_MONTHS, monthsDifference);
}

// Returns the largest BottomQuartileCategory. If no valid category is found,
// or if metricSummary is undefined, defaults to TWENTY_FIVE.
export function getLargestBottomPercentile(summary: AssociateCardSummary) {
  const categories =
    summary.bottomQuartileMetrics?.metricSummary
      ?.map(bqMetricSummary => bqMetricSummary?.label)
      .filter((item): item is string => !!item) ?? [];

  return categories.includes(BottomQuartileCategory.TWENTY_FIVE)
    ? BottomQuartileCategory.TWENTY_FIVE
    : categories.includes(BottomQuartileCategory.TEN)
      ? BottomQuartileCategory.TEN
      : categories.includes(BottomQuartileCategory.ONE)
        ? BottomQuartileCategory.ONE
        : BottomQuartileCategory.TWENTY_FIVE;
}

export function getBottomQuartileMetrics(
  summary: AssociateCardSummary
): string[] {
  return filterCCXMetricsForRemoval(
    summary.bottomQuartileMetrics?.metricSummary
      .map(summary => summary?.metric)
      .filter((item): item is string => !!item) ?? []
  );
}

export const getDirectReportViewSubtitleMessage = (
  dateSelection: string,
  directReportType: string,
  customDate?: boolean
): JSX.Element => {
  if (customDate) {
    return (
      <FormattedMessage
        id='cs_perfportal_metrics_tab_subtitle_custom_view'
        defaultMessage={`Showing individual {directReportType} trends over custom date range.`}
        values={{ directReportType }}
      />
    );
  }

  switch (dateSelection) {
    case DateCompare.DAY:
      return (
        <FormattedMessage
          id='cs_perfportal_metrics_tab_subtitle_days_view'
          defaultMessage='Showing individual {directReportType} trends over the past 15 days.'
          values={{ directReportType }}
        />
      );
    case DateCompare.WEEK:
      return (
        <FormattedMessage
          id='cs_perfportal_metrics_tab_subtitle_weeks_view'
          defaultMessage='Showing individual {directReportType} trends over the past 8 weeks.'
          values={{ directReportType }}
        />
      );
    case DateCompare.MONTH:
    default:
      return (
        <FormattedMessage
          id='cs_perfportal_metrics_tab_subtitle_months_view'
          defaultMessage='Showing individual {directReportType} trends over the past 2 months.'
          values={{ directReportType }}
        />
      );
  }
};

export const getTeamViewSubtitleMessage = (
  dateSelection: string,
  customDate?: boolean
): JSX.Element => {
  if (customDate) {
    return (
      <FormattedMessage
        id='cs_perfportal_metrics_tab_subtitle_custom'
        defaultMessage={`Showing aggregated data of the team's trends over custom date range.`}
      />
    );
  }

  switch (dateSelection) {
    case DateCompare.DAY:
      return (
        <FormattedMessage
          id='cs_perfportal_metrics_tab_subtitle_days'
          defaultMessage="Showing aggregated data of the team's trends over the past 15 days."
        />
      );
    case DateCompare.WEEK:
      return (
        <FormattedMessage
          id='cs_perfportal_metrics_tab_subtitle_weeks'
          defaultMessage="Showing aggregated data of the team's trends over the past 8 weeks."
        />
      );
    case DateCompare.MONTH:
    default:
      return (
        <FormattedMessage
          id='cs_perfportal_metrics_tab_subtitle_months'
          defaultMessage="Showing aggregated data of the team's trends over the past 2 months."
        />
      );
  }
};

export const getDateSelectionColumnHeader = (dateSelection: string): string => {
  switch (dateSelection) {
    case DateCompare.DAY:
      return 'Day';
    case DateCompare.WEEK:
      return 'Week';
    case DateCompare.MONTH:
      return 'Month';
    default:
      return 'Day';
  }
};

/** CCX-related MetricsOption values to remove when enableCCXRemoval feature flag is active */
const CCX_METRICS_TO_REMOVE = new Set<string>([
  MetricsOption.CCX,
  MetricsOption.CCX_NEG,
  MetricsOption.CCX_NEG_ONEBOX,
]);

/**
 * Filters out all CCX-related metrics when the enableCCXRemoval feature flag is enabled.
 * Uses the same generic pattern as filterCCXNeg for consistency.
 */
export const filterCCXMetricsForRemoval = <T,>(
  metrics: T[],
  getter?: (arg0: T) => string | undefined | null
): T[] => {
  const enableCCXRemoval = isFeatureEnabled(Feature.ENABLE_CCX_REMOVAL);
  if (!enableCCXRemoval) return metrics;

  return metrics.filter(metric => {
    const metricToCompare = getter ? getter(metric) : metric;
    return !CCX_METRICS_TO_REMOVE.has(metricToCompare as string);
  });
};

// in AETUtils, our trendMetricsMap contains both CCX_NEG_ONEBOX and CCX_NEG (bottom 2 box)
// this is to simplify conditionally showing ONEBOX to non WC approved regions + GO and CCX_NEG to everyone else
// this helper function implements this logic
export const filterCCXNeg = <T,>(
  metrics: T[],
  getter?: (arg0: T) => string | undefined | null
) => {
  const shouldShowTwoBox = isFeatureEnabled(Feature.CCX_BOTTOM_TWO_BOX);

  return metrics.filter(metric => {
    const metricToCompare = getter ? getter(metric) : metric;

    if (metricToCompare === MetricsOption.CCX_NEG_ONEBOX && shouldShowTwoBox) {
      return false;
    }
    if (metricToCompare === MetricsOption.CCX_NEG && !shouldShowTwoBox) {
      return false;
    }

    return true;
  });
};

export function getTimeOptions(data: TrendAndDetailsMetrics[]): string[] {
  if (!data || data.length === 0) {
    return [];
  }

  const firstMetricData =
    data[0]?.metricDatas?.trend?.[0]?.trendDatapoint ?? [];

  if (!Array.isArray(firstMetricData)) {
    return [];
  }

  const timestamps = firstMetricData.map(
    (datapoint: any) => datapoint?.timestamp ?? ''
  );

  const sortedTimestamps = timestamps.sort((a, b) => {
    return b.localeCompare(a);
  });

  return sortedTimestamps;
}
